// src/ChatInterface.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from './supabaseClient';
import axios from 'axios';
import UserSettingsSidebar from './components/UserSettingsSidebar';
import DefaultPromptModal from './components/DefaultPromptModal';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Link from '@mui/material/Link';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { v4 as uuidv4 } from 'uuid';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsIcon from '@mui/icons-material/Settings'; // Added for Left Side Settings
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import DescriptionIcon from '@mui/icons-material/Description';
import LinkIcon from '@mui/icons-material/Link';
import { useNavigate } from 'react-router-dom';

import {
  Container,
  Box,
  Typography,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TextField,
  CircularProgress,
  IconButton,
  Drawer,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';

import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import UserMenu from './components/UserMenu';
import LeftNavDrawer from './components/LeftNavDrawer';
import InviteMemberDialog from './components/InviteMemberDialog';
import ChatwootConfigDialog from './components/ChatwootConfigDialog';
import LocalChatwootSetupDialog from './components/LocalChatwootSetupDialog';
import DeleteIcon from '@mui/icons-material/Delete';
import Tooltip from '@mui/material/Tooltip';
import InputAdornment from '@mui/material/InputAdornment';
import Typewriter from './Typewriter';
import SendIcon from '@mui/icons-material/Send';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import StopIcon from '@mui/icons-material/Stop';

// Define Backend URL
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const isHttpUrl = (string) => {
  if (!string) return false;
  try {
    const url = new URL(string);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch (_) {
    return false;
  }
}

function preprocessSourceLinks(text) {
  // --- ADD NULL CHECK ---
  if (text === null || typeof text === 'undefined') {
    return ''; // Return empty string if input is null/undefined
  }
  // --- END NULL CHECK ---

  // Replace [Source: filename|url] with [filename](url) if url is not '#'
  return text.replace(/\[Source: ([^\|\]]+)\|([^\]]+)\]/g, (match, filename, url) => {
    if (url && url !== '#') {
      return `[${filename}](${url})`;
    } else {
      return filename;
    }
  });
}

// Stable empty array to avoid re-renders when no conversation exists
const STABLE_EMPTY_ARRAY = Object.freeze([]);

// Memoized message list to prevent re-renders on keystrokes
const MessageList = React.memo(function MessageList({ conversation }) {
  return (
    <>
      {conversation.map((message, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
            mb: 1,
          }}
        >
          <Box
            sx={{
              p: 1,
              borderRadius: '16px',
              backgroundColor: message.sender === 'user' ? '#F5F5F5' :
                (message.mode === 'direct' ? '#E3F2FD' : '#E8E8E8'),
              maxWidth: '80%',
              position: 'relative'
            }}
          >
            {message.mode === 'direct' && (
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  top: -15,
                  right: 5,
                  color: '#1976d2',
                  fontSize: '0.7rem',
                  fontWeight: 'bold'
                }}
              >
                Direct Mode
              </Typography>
            )}
            {message.sender === 'user' ? (
              <Typography variant="body1">{message.text}</Typography>
            ) : (
              (() => {
                const markdownInput = preprocessSourceLinks(message.text);
                if (typeof markdownInput !== 'string') {
                  return <Typography color="error">Error displaying message content.</Typography>;
                }
                return (
                  <ReactMarkdown
                    components={{
                      p: ({ node, ...props }) => (
                        <Typography variant="body1" {...props} sx={{ marginBottom: 1 }} />
                      ),
                      strong: ({ node, ...props }) => (
                        <Typography component="span" sx={{ fontWeight: 'bold' }} {...props} />
                      ),
                      a: ({ node, ...props }) => (
                        <Link href={props.href} target="_blank" rel="noopener noreferrer" {...props} />
                      )
                    }}
                  >
                    {markdownInput}
                  </ReactMarkdown>
                );
              })()
            )}
          </Box>
        </Box>
      ))}
    </>
  );
});

function ChatInterface({ handleLogout }) {
  console.log('[ChatInterface] handleLogout prop:', handleLogout);
  // State variables
  const [question, setQuestion] = useState('');
  const [sessions, setSessions] = useState({});
  const [activeSessionId, setActiveSessionId] = useState(() => {
    try { return localStorage.getItem('active_session_id') || null; } catch (_) { return null; }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [directMode, setDirectMode] = useState(false);
  const [useWebSearch, setUseWebSearch] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [fileListSidebarOpen, setFileListSidebarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [defaultPromptModalOpen, setDefaultPromptModalOpen] = useState(false);
  const [userId, setUserId] = useState(null);

  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isWebSearching, setIsWebSearching] = useState(false);
  const [navSidebarOpen, setNavSidebarOpen] = useState(false);

  // --- NEW: State for File Content Dialog ---
  const [isContentDialogOpen, setIsContentDialogOpen] = useState(false);
  const [selectedFileContent, setSelectedFileContent] = useState('');
  const [loadingFileContent, setLoadingFileContent] = useState(false);
  const [selectedFileForContent, setSelectedFileForContent] = useState(null);
  // --- END NEW ---

  // --- NEW: State for Rename Dialog --- 
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [sessionToRename, setSessionToRename] = useState(null); // Stores {id, currentName}
  const [newSessionName, setNewSessionName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameError, setRenameError] = useState('');
  // ------------------------------------

  // --- NEW: State for available scopes (company/teams) ---
  const [userCompany, setUserCompany] = useState(null); // { id, name }
  const [userTeams, setUserTeams] = useState([]);       // [{ id, name, company_id }, ...]
  const [isLoadingScopes, setIsLoadingScopes] = useState(false);
  // --- END NEW ---

  // --- NEW: Drawer-triggered dialogs (invite & integrations) ---
  const [leftNavInviteOpen, setLeftNavInviteOpen] = useState(false);
  const [leftNavChatwootOpen, setLeftNavChatwootOpen] = useState(false);
  const [leftNavLocalChatwootOpen, setLeftNavLocalChatwootOpen] = useState(false);

  const handleSendInviteFromLeftNav = async (inviteData) => {
    try {
      const { data, error: invokeError } = await supabase.functions.invoke('send-team-invitation', { body: inviteData });
      if (invokeError) throw invokeError;
      if (data && data.error) {
        const err = new Error(data.details || data.error || 'Invitation failed.');
        throw err;
      }
      return data;
    } catch (err) {
      throw err;
    }
  };

  const navigate = useNavigate(); // Initialize navigate

  // Auth token helper (placed before auto-name hook to avoid TDZ)
  const getAuthToken = useCallback(async () => {
    console.log("[ChatInterface getAuthToken] CALLED");
    try {
      // Poll briefly to allow OAuth session hydration to complete
      let attempts = 0;
      while (attempts < 20) { // up to ~6s with 300ms delay
        const sessionResult = await supabase.auth.getSession();
        try {
          console.log("[ChatInterface getAuthToken] supabase.auth.getSession() result:", JSON.stringify(sessionResult));
        } catch (_) {
          console.log("[ChatInterface getAuthToken] supabase.auth.getSession() result (non-JSON)", sessionResult);
        }

        if (sessionResult?.error) {
          console.error("[ChatInterface getAuthToken] Auth error getting session:", sessionResult.error);
          // break early on hard auth errors
          throw new Error(`Authentication error: ${sessionResult.error.message}`);
        }

        const token = sessionResult?.data?.session?.access_token;
        if (token) {
          console.log("[ChatInterface getAuthToken] Access token found.");
          return token;
        }

        // If URL has OAuth code, give more time for session hydration
        if (typeof window !== 'undefined' && window.location.search.includes('code=')) {
          await new Promise(r => setTimeout(r, 300));
          attempts += 1;
          continue;
        } else {
          // No OAuth flow - no token, stop early
          break;
        }
      }

      console.log("[ChatInterface getAuthToken] No active session or access token found after wait.");
      throw new Error("No active session or access token available.");
    } catch (e) {
      console.error("[ChatInterface getAuthToken] Exception in getAuthToken:", e);
      throw e;
    }
  }, []);

  // --- Auto name helpers ---
  const generateTitleFromText = useCallback((text) => {
    if (!text || typeof text !== 'string') return 'New chat';
    const cleaned = text
      .replace(/\s+/g, ' ')
      .replace(/[\r\n]+/g, ' ')
      .trim();
    // Prefer first sentence or question
    const firstStop = cleaned.split(/(?<=[\.!?])\s+/)[0] || cleaned;
    const words = firstStop.split(' ').slice(0, 8);
    const raw = words.join(' ');
    const title = raw.length > 60 ? raw.slice(0, 57).trim() + '…' : raw;
    // Basic title casing without forcing locale changes
    return title.replace(/(^|\s)([a-z])/g, (m) => m.toUpperCase());
  }, []);

  const maybeAutoNameSession = useCallback(async (sessionId, questionText) => {
    try {
      const current = sessions[sessionId];
      if (!current) return;
      const currentName = current.name || '';
      const isDefault = (!currentName) || /^(chat( session)?)/i.test(currentName);
      const hasMessages = Array.isArray(current.conversation) && current.conversation.length > 0;
      if (!isDefault || hasMessages === true) return; // only once, on first message, and only if default

      const proposed = generateTitleFromText(questionText);
      if (!proposed || proposed.length < 3) return;

      const token = await getAuthToken();
      const resp = await axios.put(`${BACKEND_URL}/api/sessions/${sessionId}`, { name: proposed }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resp?.data) {
        setSessions(prev => ({
          ...prev,
          [sessionId]: { ...prev[sessionId], name: resp.data.name || proposed }
        }));
      }
    } catch (e) {
      console.warn('[auto-name] Failed to auto-name session:', e?.message || e);
    }
  }, [sessions, getAuthToken]);

  // Fetch userId and check admin status when the component mounts
  useEffect(() => {
    async function fetchUserAndAdminStatus() {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (user) {
        setUserId(user.id);
        // Check for admin role
        const isUserAdmin = user?.app_metadata?.app_role === 'admin' || user?.raw_app_meta_data?.app_role === 'admin';
        setIsAdmin(isUserAdmin);
        console.log(`[ChatInterface] User ID: ${user.id}, Is Admin: ${isUserAdmin}`);
      } else if (error) {
        console.error("Error fetching user in ChatInterface:", error.message);
        // Handle error, maybe redirect to login if persistent?
      }
    }
    fetchUserAndAdminStatus();
  }, []); // Runs once on mount

  // --- NEW: useEffect to fetch user's company and teams ---
  useEffect(() => {
    const fetchUserScopeData = async () => {
      if (!userId || !getAuthToken) return;

      console.log("[ChatInterface] Fetching user company and teams data...");

      setIsLoadingScopes(true);
      console.log("[ChatInterface] Fetching user company and teams data...");
      try {
        const token = await getAuthToken();

        // Fetch company data
        // Replace with your actual API endpoint for company details
        const companyResponse = await axios.get(`${BACKEND_URL}/api/v1/user/company`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (companyResponse.data) {
          setUserCompany(companyResponse.data); // Assuming API returns { id, name }
          console.log("[ChatInterface] User company data fetched:", companyResponse.data);
        } else {
          setUserCompany(null); // No company associated or error
          console.log("[ChatInterface] No company data found for user.");
        }

        // Fetch teams data
        // Replace with your actual API endpoint for user's teams
        const teamsResponse = await axios.get(`${BACKEND_URL}/api/v1/user/teams`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (teamsResponse.data && Array.isArray(teamsResponse.data)) {
          setUserTeams(teamsResponse.data); // Assuming API returns [{ id, name, company_id }, ...]
          console.log("[ChatInterface] User teams data fetched:", teamsResponse.data);
        } else {
          setUserTeams([]); // No teams or error
          console.log("[ChatInterface] No teams data found for user.");
        }

      } catch (error) {
        console.error("[ChatInterface] Error fetching user scope data (company/teams):", error);
        setError("Could not load your company/team information for scope selection.");
        setUserCompany(null);
        setUserTeams([]);
        // Do NOT logout on 401/403 here; allow session to stabilize
        if (error.response?.status === 401 || error.response?.status === 403) {
          console.warn("[ChatInterface] 401/403 during scope fetch. Suppressing logout and will retry later.");
        }
      } finally {
        setIsLoadingScopes(false);
      }
    };

    fetchUserScopeData();
  }, [userId, getAuthToken, handleLogout]); // Dependencies
  // --- END NEW ---

  const fetchSessions = useCallback(async () => {
    if (!userId) return;
    setIsLoadingSessions(true);
    setError('');
    console.log("[ChatInterface] Fetching session list...");
    try {
      const token = await getAuthToken();
      const response = await axios.get(`${BACKEND_URL}/api/sessions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const fetchedSessions = response.data || [];

      setSessions(prevLocalSessions => {
        const newSessionsMap = fetchedSessions.reduce((acc, remoteSession) => {
          acc[remoteSession.id] = {
            ...remoteSession,
            conversation: prevLocalSessions[remoteSession.id]?.conversation,
            selectedFiles: prevLocalSessions[remoteSession.id]?.selectedFiles || [],
            selectedFileHashes: prevLocalSessions[remoteSession.id]?.selectedFileHashes || []
          };
          return acc;
        }, {});

        console.log("[ChatInterface fetchSessions] newSessionsMap to be set:", newSessionsMap);
        return newSessionsMap;
      });

      setActiveSessionId(prevActiveSessionId => {
        if (fetchedSessions.length > 0) {
          let currentActiveSessionIsValid = false;
          if (prevActiveSessionId) {
            currentActiveSessionIsValid = fetchedSessions.some(s => s.id === prevActiveSessionId);
          }

          if (!prevActiveSessionId || !currentActiveSessionIsValid) {
            const stored = (() => { try { return localStorage.getItem('active_session_id'); } catch (_) { return null; } })();
            let sessionToActivate = stored && fetchedSessions.some(s => s.id === stored) ? stored : fetchedSessions[0].id;
            console.log("[ChatInterface] No valid active session or active session not in new list. Activating session from backend list:", sessionToActivate);
            try { localStorage.setItem('active_session_id', sessionToActivate); } catch (_) { }
            return sessionToActivate;
          } else {
            console.log("[ChatInterface] Kept existing active session:", prevActiveSessionId);
            try { localStorage.setItem('active_session_id', prevActiveSessionId); } catch (_) { }
            return prevActiveSessionId;
          }
        } else {
          let activeExistsLocally = false;
          setSessions(currentSessionsState => {
            activeExistsLocally = currentSessionsState && currentSessionsState[prevActiveSessionId];
            return currentSessionsState;
          });

          if (activeExistsLocally) {
            console.log("[ChatInterface] No sessions from backend, but a local active session exists. Keeping it.", prevActiveSessionId);
            try { if (prevActiveSessionId) localStorage.setItem('active_session_id', prevActiveSessionId); } catch (_) { }
            return prevActiveSessionId;
          } else {
            console.log("[ChatInterface] No sessions found from backend and no valid local active session. Setting active to null.");
            setError("No chat sessions found. Click 'New Chat' to start.");
            try { localStorage.removeItem('active_session_id'); } catch (_) { }
            return null;
          }
        }
      });

    } catch (error) {
      console.error("[ChatInterface] Error fetching sessions:", error);
      setError("Failed to load chat sessions.");
      // Do NOT logout on 401/403; backend may still be initializing token verification
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.warn("[ChatInterface] 401/403 during sessions fetch. Suppressing logout.");
      }
    } finally {
      setIsLoadingSessions(false);
    }
  }, [userId, getAuthToken, handleLogout, setIsLoadingSessions, setError, setSessions, setActiveSessionId]);

  const fetchSessionHistory = useCallback(async (sessionId) => {
    if (!sessionId) return;
    console.log(`[ChatInterface] Attempting to fetch history for session: ${sessionId}`);
    setError('');
    try {
      const token = await getAuthToken();
      const response = await axios.get(`${BACKEND_URL}/api/sessions/${sessionId}/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const rawHistory = response.data?.history || response.data || [];
      console.log(`[ChatInterface] Successfully fetched raw history for ${sessionId}:`, rawHistory);

      // Transform backend history (role, content) to frontend format (sender, text)
      const formattedHistory = rawHistory.map(msg => ({
        sender: msg.role,   // Map 'role' from backend to 'sender' for UI
        text: msg.content,  // Map 'content' from backend to 'text' for UI
        // Assuming your UI might use a 'mode' property later, you can infer or default it
        mode: msg.role === 'user' ? 'rag' : (msg.metadata?.mode || (msg.text && msg.text.includes("[Source:") ? 'rag' : 'direct'))
        // Add other properties if your UI expects them e.g. unique id for mapping if not using index
        // id: msg.db_id || uuidv4() // If your backend provides an id for each message
      }));
      console.log(`[ChatInterface] Formatted history for UI for ${sessionId}:`, formattedHistory);

      setSessions(prev => {
        if (!prev[sessionId]) {
          console.warn(`[ChatInterface] Session ${sessionId} not found in prev state during history update.`);
          return prev;
        }
        return {
          ...prev,
          [sessionId]: {
            ...prev[sessionId],
            conversation: formattedHistory // Use the transformed history
          }
        }
      });
    } catch (error) {
      console.error(`[ChatInterface] Error fetching history for session ${sessionId}:`, error);
      setError(`Failed to load chat history for session ID ${sessionId.substring(0, 8)}.`);
      // Suppress logout on 401/403 here as well
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.warn("[ChatInterface] 401/403 during history fetch. Suppressing logout.");
      }
    }
  }, [getAuthToken, handleLogout, sessions, setError]);

  const handleCreateSession = useCallback(async () => {
    console.log("[ChatInterface] Creating new session via API...");
    setIsLoadingSessions(true);

    // --- NEW: Quota Check ---
    if (userCompany && userCompany.id) {
      try {
        const { data: canCreate, error: checkError } = await supabase
          .rpc('can_create_new_session', { p_company_id: userCompany.id });

        if (checkError) {
          console.error("[ChatInterface] Error checking session quota:", checkError);
          setError("Could not verify your session quota. Please try again.");
          setIsLoadingSessions(false);
          return;
        }

        if (canCreate === false) {
          setError("You have reached your monthly session limit for this company. Please upgrade your plan or contact support.");
          setIsLoadingSessions(false);
          return;
        }
      } catch (rpcError) {
        console.error("[ChatInterface] RPC call to can_create_new_session failed:", rpcError);
        setError("An unexpected error occurred while checking your session quota.");
        setIsLoadingSessions(false);
        return;
      }
    }
    // --- END: Quota Check ---

    const token = await getAuthToken();
    const previousActiveSessionId = activeSessionId;

    try {
      const response = await axios.post(`${BACKEND_URL}/api/sessions`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newSession = response.data;

      if (newSession && newSession.id) {
        // --- NEW: Increment Session Quota Count ---
        if (userCompany && userCompany.id) {
          const { error: incrementError } = await supabase
            .rpc('increment_session_count', { p_company_id: userCompany.id });

          if (incrementError) {
            // Non-blocking error: The session was created, but quota failed to update.
            // This should be logged for monitoring.
            console.error('[ChatInterface] CRITICAL: Failed to increment session count:', incrementError);
            setError("Warning: Could not update session usage count."); // Inform user, but don't block.
          }
        }
        // --- END: Increment ---

        console.log("[ChatInterface] New session created on backend:", newSession);
        let settingsToInherit = null;
        if (previousActiveSessionId) {
          console.log(`[ChatInterface] Attempting to inherit settings from previous session: ${previousActiveSessionId}`);
          const { data: prevSessionSettings, error: prevSessionError } = await supabase
            .from('session_settings')
            .select('*')
            .eq('session_id', previousActiveSessionId)
            .maybeSingle();
          if (prevSessionError) {
            console.warn(`[ChatInterface] Error fetching settings for previous session ${previousActiveSessionId}:`, prevSessionError);
          } else if (prevSessionSettings) {
            settingsToInherit = { ...prevSessionSettings };
            console.log(`[ChatInterface] Inherited settings from previous session ${previousActiveSessionId}:`, settingsToInherit);
          }
        }
        if (!settingsToInherit && userId) {
          console.log(`[ChatInterface] No previous session settings. Attempting to inherit from user_settings for user: ${userId}`);
          const { data: userDefaultSettings, error: userDefaultError } = await supabase
            .from('user_settings')
            .select('embedding_model, system_prompt, llm_model, temperature, top_p, total_chunks, answer_max_token, top_k, cosine_similarity_threshold, rag_mode, enable_grounding_check, grounding_similarity_threshold, enable_reranker, direct_system_prompt')
            .eq('user_id', userId)
            .maybeSingle();
          if (userDefaultError) {
            console.warn(`[ChatInterface] Error fetching user_settings for user ${userId}:`, userDefaultError);
          } else if (userDefaultSettings) {
            settingsToInherit = { ...userDefaultSettings };
            console.log(`[ChatInterface] Inherited settings from user_settings for user ${userId}:`, settingsToInherit);
          }
        }
        if (!settingsToInherit) {
          console.log("[ChatInterface] No previous session or user settings found. Fetching system defaults from database.");
          try {
            const systemDefaultsResponse = await axios.get(`${BACKEND_URL}/api/system-defaults`);
            if (systemDefaultsResponse.data) {
              settingsToInherit = systemDefaultsResponse.data;
              console.log("[ChatInterface] Successfully fetched system default settings from database:", settingsToInherit);
            } else {
              throw new Error("Empty response from system-defaults endpoint");
            }
          } catch (systemDefaultsError) {
            console.warn("[ChatInterface] Failed to fetch system defaults from database, using hardcoded fallback:", systemDefaultsError);
            // Final fallback to hardcoded values if API call fails
            settingsToInherit = {
              embedding_model: 'bge-m3-embedding',
              system_prompt: "You are an AI assistant that provides helpful and concise answers based solely on the provided document and conversation history. Use the documents and the conversation history to understand the user's question and provide a relevant and accurate answer. Do not include verbatim quotes or references to previous messages or questions in your answer. Do NOT MENTION ANY PREVIOUS CHAT IN YOUR ANSWER. Do not repeat any previous chat in your answer. DO NOT USE ANY EXTERNAL KNOWLEDGE OR YOUR PRETRAINED INFORMATION. DO NOT USE ANY INFORMATION NOT PRESENT IN THE DOCUMENT. Focus on providing concise, relevant, clear and accurate responses from the context. If you cannot find the answer in the context or conversation history, state that the information is not available and do not offer additional assistance or ask follow-up questions.",
              llm_model: process.env.REACT_APP_DEFAULT_LLM_MODEL || 'gemini-2.0-flash',
              temperature: 0.0,
              top_p: 1.0,
              total_chunks: 50,
              answer_max_token: 512,
              top_k: 15,
              cosine_similarity_threshold: 0.3,
              rag_mode: 'similarity_filter',
              enable_grounding_check: false,
              grounding_similarity_threshold: 0.4,
              enable_reranker: true,
              direct_system_prompt: "You are a helpful AI assistant. Answer the user's question directly."
            };
          }
        }
        const newSessionSettings = {
          ...settingsToInherit,
          session_id: newSession.id,
        };
        delete newSessionSettings.id;
        delete newSessionSettings.user_id;
        delete newSessionSettings.created_at;
        delete newSessionSettings.updated_at;
        if (typeof newSessionSettings.temperature === 'string') {
          newSessionSettings.temperature = parseFloat(newSessionSettings.temperature);
        }
        console.log(`[ChatInterface] Attempting to insert/upsert settings for new session ${newSession.id}:`, newSessionSettings);
        const { error: upsertError } = await supabase
          .from('session_settings')
          .upsert(newSessionSettings, { onConflict: 'session_id' });
        if (upsertError) {
          console.error(`[ChatInterface] Error inserting/upserting session_settings for new session ${newSession.id}:`, upsertError);
        } else {
          console.log(`[ChatInterface] Successfully inserted/upserted session_settings for new session ${newSession.id}.`);
        }
        const newSessionEntry = {
          ...newSession,
          conversation: [],
          selectedFiles: [],
          selectedFileHashes: []
        };
        setSessions(prev => ({
          ...prev,
          [newSession.id]: newSessionEntry
        }));
        setActiveSessionId(newSession.id);
        try { localStorage.setItem('active_session_id', newSession.id); } catch (_) { }
        setQuestion('');
        setError('');
        setNavSidebarOpen(false);
        fetchSessions();
      } else {
        throw new Error("Invalid response from create session API");
      }
    } catch (error) {
      console.error("[ChatInterface] Error creating new session or its settings:", error);
      setError("Failed to create new chat session or apply settings.");
      // Suppress logout on 401/403 during session creation; allow retry after auth stabilizes
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.warn("[ChatInterface] 401/403 during session creation. Suppressing logout.");
      }
    } finally {
      setIsLoadingSessions(false);
    }
  }, [getAuthToken, activeSessionId, userId, handleLogout, setSessions, setActiveSessionId, setError, setQuestion, setIsLoadingSessions, fetchSessions, userCompany]);

  const handleSelectSession = useCallback((sessionId) => {
    if (sessionId !== activeSessionId) {
      console.log("[ChatInterface] User selected session:", sessionId);
      setActiveSessionId(sessionId); // This will trigger the useEffect for history loading
      try { localStorage.setItem('active_session_id', sessionId); } catch (_) { }
    }
  }, [activeSessionId, setActiveSessionId]);

  // useEffect for initial session fetch when userId changes
  useEffect(() => {
    if (userId) {
      console.log("[ChatInterface Effect] userId changed or component mounted with userId. Fetching sessions.");
      fetchSessions();
    }
  }, [userId, fetchSessions]); // fetchSessions is now more stable due to removed internal session dependency

  // useEffect to fetch history when activeSessionId changes AND that session's history is not yet defined
  useEffect(() => {
    if (activeSessionId && sessions[activeSessionId] &&
      sessions[activeSessionId].conversation === undefined) { // More specific check
      console.log(`[ChatInterface Effect - History Fetch] Active session is ${activeSessionId}. Its history is undefined. Fetching...`);
      fetchSessionHistory(activeSessionId);
    }
  }, [activeSessionId, sessions, fetchSessionHistory]);

  // useEffect to clear question and error ONLY when activeSessionId actually changes
  useEffect(() => {
    console.log(`[ChatInterface Effect - Input Clear] Active session changed to ${activeSessionId}. Clearing inputs.`);
    setQuestion('');
    setError('');
  }, [activeSessionId, setQuestion, setError]); // Only depends on activeSessionId (and the setters)

  // useEffect to create a session if none exist after loading.
  useEffect(() => {
    if (!isLoadingSessions && sessions && Object.keys(sessions).length === 0) {
      console.log("[ChatInterface Effect] No sessions found after loading. Creating a new one.");
      handleCreateSession();
    }
  }, [isLoadingSessions, sessions, handleCreateSession]);

  // Add the handleCheckboxChange function
  const handleCheckboxChange = useCallback((event, filename) => {
    const isChecked = event.target.checked;
    setSessions(prev => {
      if (!prev[activeSessionId]) return prev;

      // Find the full file object from uploadedFiles to get its hash
      const fileObject = uploadedFiles.find(f => f.filename === filename);
      if (!fileObject || !fileObject.file_hash) {
        console.warn(`[ChatInterface] File object or file_hash not found for filename: ${filename} during checkbox change. Cannot update selectedFileHashes.`);
        // Optionally, update only selectedFiles (filenames) if hash is missing, or just return prev.
        // For now, let's proceed to update selectedFiles (filenames) as before, but log the warning.
        // If file_hash is critical, you might want to prevent selection or handle this more strictly.
        const currentSelectedFiles = prev[activeSessionId].selectedFiles || [];
        let newSelectedFilesByName;
        if (isChecked) {
          newSelectedFilesByName = [...new Set([...currentSelectedFiles, filename])];
        } else {
          newSelectedFilesByName = currentSelectedFiles.filter(f => f !== filename);
        }
        return {
          ...prev,
          [activeSessionId]: {
            ...prev[activeSessionId],
            selectedFiles: newSelectedFilesByName
          }
        };
      }

      const fileHash = fileObject.file_hash;

      const currentSelectedFiles = prev[activeSessionId].selectedFiles || [];
      const currentSelectedHashes = prev[activeSessionId].selectedFileHashes || [];

      let newSelectedFilesByName;
      let newSelectedHashes;

      if (isChecked) {
        newSelectedFilesByName = [...new Set([...currentSelectedFiles, filename])];
        newSelectedHashes = [...new Set([...currentSelectedHashes, fileHash])];
      } else {
        newSelectedFilesByName = currentSelectedFiles.filter(f => f !== filename);
        newSelectedHashes = currentSelectedHashes.filter(h => h !== fileHash);
      }

      return {
        ...prev,
        [activeSessionId]: {
          ...prev[activeSessionId],
          selectedFiles: newSelectedFilesByName,
          selectedFileHashes: newSelectedHashes // Store selected hashes
        }
      };
    });
  }, [activeSessionId, uploadedFiles]); // Added uploadedFiles as a dependency

  // Add handler for select all files
  const handleSelectAllFiles = useCallback((isChecked) => {
    setSessions(prev => {
      if (!prev[activeSessionId]) return prev;

      let newSelectedFiles = [];
      let newSelectedFileHashes = []; // NEW: For file hashes

      if (isChecked) {
        // Select all *available* uploaded files that are completed
        const completedFiles = uploadedFiles.filter(file => file.processing_status === 'completed');
        newSelectedFiles = completedFiles.map(file => file.filename);
        newSelectedFileHashes = completedFiles.map(file => file.file_hash).filter(hash => hash); // Ensure hash exists
      }
      // If not checked, newSelectedFiles and newSelectedFileHashes remain empty []

      return {
        ...prev,
        [activeSessionId]: {
          ...prev[activeSessionId],
          selectedFiles: newSelectedFiles,
          selectedFileHashes: newSelectedFileHashes // Store selected hashes
        }
      };
    });
  }, [activeSessionId, uploadedFiles]); // Added uploadedFiles as a dependency

  // Add handler for selecting files by team
  const handleSelectFilesByTeam = useCallback((teamId) => {
    if (!teamId) return;

    setSessions(prev => {
      if (!prev[activeSessionId]) return prev;

      // Filter completed files that belong to the selected team
      const teamFiles = uploadedFiles.filter(file =>
        file.processing_status === 'completed' && file.team_id === teamId
      );

      const newSelectedFiles = teamFiles.map(file => file.filename);
      const newSelectedFileHashes = teamFiles.map(file => file.file_hash).filter(hash => hash);

      return {
        ...prev,
        [activeSessionId]: {
          ...prev[activeSessionId],
          selectedFiles: newSelectedFiles,
          selectedFileHashes: newSelectedFileHashes
        }
      };
    });
  }, [activeSessionId, uploadedFiles]);

  // Extract unique teams from uploaded files
  const availableTeams = React.useMemo(() => {
    const teamsMap = new Map();
    uploadedFiles.forEach(file => {
      if (file.team_id && file.processing_status === 'completed') {
        // Backend extracts team name and provides it as 'team_name' property
        const teamName = file.team_name || `Team ${file.team_id.substring(0, 8)}`;
        teamsMap.set(file.team_id, teamName);
      }
    });
    return Array.from(teamsMap.entries()).map(([id, name]) => ({ id, name }));
  }, [uploadedFiles]);

  // Add function to check if all completed files are selected
  const areAllCompletedFilesSelected = () => {
    const completedFiles = uploadedFiles.filter(file => file.processing_status === 'completed');
    return completedFiles.length > 0 && completedFiles.every(file => file.selected);
  };

  // Add function to check if some completed files are selected
  const areSomeCompletedFilesSelected = () => {
    const completedFiles = uploadedFiles.filter(file => file.processing_status === 'completed');
    return completedFiles.some(file => file.selected) && !areAllCompletedFilesSelected();
  };

  // Reference for auto-scrolling
  const conversationEndRef = useRef(null);
  // Uncontrolled input ref for question field
  const questionInputRef = useRef(null);
  // Track current WebSocket to allow cancellation
  const wsRef = useRef(null);
  // Track if stop was requested by the user to suppress error alerts on intentional close
  const stopRequestedRef = useRef(false);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAskQuestion(e);
    }
  };

  // Fetch uploaded files when the component mounts
  useEffect(() => {
    const fetchUploadedFiles = async () => {
      console.log("Entering fetchUploadedFiles");
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          const token = session ? session.access_token : '';
          const response = await fetch(`${BACKEND_URL}/get_uploaded_files`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          console.log("Response received from fetch:", response);
          if (response.status === 200) {
            const data = await response.json();
            console.log("Fetched data:", data);
            const filesArray = Array.isArray(data.files) ? data.files : [];
            console.log("Normalized files array:", filesArray);
            if (filesArray.length > 0) {
              setUploadedFiles(
                filesArray.map((file) => ({
                  ...file,
                  selected: false,
                  processing_status: file.processing_status || 'completed',
                }))
              );
            } else {
              setUploadedFiles([]);
              console.log("No files found for user");
            }
          } else {
            console.error("Error fetching uploaded files:", response.statusText);
            setError('Failed to fetch your files.');
            setUploadedFiles([]);
          }
        } catch (error) {
          console.error("Error fetching files from external service:", error);
          setError('Failed to fetch your files.');
          setUploadedFiles([]);
        }
      } else {
        setError('Please log in to view your files.');
      }
    };
    fetchUploadedFiles();
  }, []);

  // Auto-scroll to the bottom when the conversation updates
  useEffect(() => {
    if (conversationEndRef.current) {
      conversationEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeSessionId, sessions[activeSessionId]?.conversation?.length]);

  // Function to toggle the sidebar
  const toggleFileListSidebar = (open) => (event) => {
    if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) return;
    setFileListSidebarOpen(open);
  };

  const handleOpenSettingsSidebar = () => {
    console.log(`[ChatInterface] handleOpenSettingsSidebar called. Current activeSessionId: ${activeSessionId}, userId: ${userId}`);
    setSettingsOpen(true);
  };

  const handleCloseSettingsSidebar = () => {
    setSettingsOpen(false);
  };

  const handleOpenDefaultPrompt = () => {
    console.log(`[ChatInterface] handleOpenDefaultPrompt called. userId: ${userId}`);
    setDefaultPromptModalOpen(true);
  };

  const handleCloseDefaultPrompt = () => {
    setDefaultPromptModalOpen(false);
  };

  // --- Fixed handleDeleteChatHistory to match backend behavior (deletes entire session) --- 
  const handleDeleteChatHistory = useCallback(async () => {
    if (!activeSessionId) {
      setError("No active session selected to delete.");
      return;
    }

    // Get session name SAFELY right before confirming
    let currentSessionName = `Chat ${activeSessionId.substring(0, 4)}`;
    try {
      if (sessions && sessions[activeSessionId]) {
        currentSessionName = sessions[activeSessionId].name || currentSessionName;
      }
    } catch (e) { console.error("Error accessing session name:", e); }

    // Confirmation dialog - Updated text to reflect that entire session is deleted
    if (window.confirm(`Are you sure you want to delete session "${currentSessionName}"? This will permanently delete the entire chat session and cannot be undone.`)) {
      console.log(`[handleDeleteChatHistory] Confirmed deletion for session: ${activeSessionId}`);

      setError('');
      setMessage('Deleting session...');

      try {
        // Call Backend DELETE Endpoint - this deletes the entire session
        const token = await getAuthToken();
        console.log(`[handleDeleteChatHistory] Calling DELETE ${BACKEND_URL}/api/sessions/${activeSessionId}`);
        await axios.delete(`${BACKEND_URL}/api/sessions/${activeSessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log(`[handleDeleteChatHistory] Backend completely deleted session ${activeSessionId}`);

        setMessage(`Session "${currentSessionName}" deleted. Creating new session...`);

        // Since backend deleted the entire session, create a new one
        await handleCreateSession();

      } catch (error) {
        console.error("[handleDeleteChatHistory] Error deleting session:", error);
        const detail = error.response?.data?.detail || "Could not delete session.";
        setError(detail);
        if (error.response?.status === 401 || error.response?.status === 403) {
          if (handleLogout) handleLogout();
        }
      } finally {
        setTimeout(() => setMessage(''), 3000);
      }
    }
  }, [activeSessionId, sessions, handleLogout, handleCreateSession, setError, setMessage]);
  // -----------------------------------------------------------------

  // --- Modify handleAskQuestion --- 
  const handleAskQuestion = async (e) => {
    e.preventDefault();
    const currentQuestion = (questionInputRef.current?.value || '').trim();

    if (!activeSessionId) {
      setError("No active chat session. Please create or select a chat.");
      return;
    }

    // --- NEW: Check message quota BEFORE sending (same pattern as session limits) ---
    if (userCompany && userCompany.id) {
      try {
        const { data: quotaCheck, error: checkError } = await supabase
          .rpc('can_send_message', { p_company_id: userCompany.id });

        if (checkError) {
          console.error("[ChatInterface] Error checking message quota:", checkError);
          setError("Could not verify your message quota. Please try again.");
          return;
        }

        // New format: quotaCheck is now a JSON object with {allowed, reason, limit_type}
        if (quotaCheck && quotaCheck.allowed === false) {
          // Use the specific reason from the DB function
          setError(quotaCheck.reason || "You have reached your message limit.");
          console.warn(`[ChatInterface] Message blocked: ${quotaCheck.limit_type} limit - Used: ${quotaCheck.used}/${quotaCheck.limit}`);
          return;
        }
      } catch (rpcError) {
        console.error("[ChatInterface] RPC call to can_send_message failed:", rpcError);
        setError("An unexpected error occurred while checking your message quota.");
        return;
      }
    }
    // --- END: Message Quota Check ---

    const { data: { session } } = await supabase.auth.getSession();

    if (session && currentQuestion) {
      const user = session.user;
      const token = session.access_token;

      // MODIFIED: Get selectedFileHashes from the active session state
      const currentSessionDetails = sessions[activeSessionId];
      const selected_file_hashes = currentSessionDetails?.selectedFileHashes || [];

      let determinedQueryScopeType = currentSessionDetails?.scope_type || 'personal';
      let determinedQueryScopeId = currentSessionDetails?.scope_id || null;

      const { data: { user: authUser } } = await supabase.auth.getUser();
      const currentUserId = authUser.id;

      if (selected_file_hashes.length > 0) {
        let allPersonal = true;
        // For simplicity, we'll base decisions on the first selected file if scopes might differ.
        // A more complex UI/UX would handle multi-scope selection explicitly.
        const firstSelectedFile = uploadedFiles.find(f => f.file_hash === selected_file_hashes[0]);

        if (firstSelectedFile) {
          if (firstSelectedFile.user_id !== currentUserId) {
            allPersonal = false;
          }

          if (!allPersonal) { // If the first selected file is not personal to the current user
            console.log("[ChatInterface] First selected file is not personal. Attempting to determine shared query scope.");
            if (firstSelectedFile.company_id && userCompany && firstSelectedFile.company_id === userCompany.id) {
              determinedQueryScopeType = 'company';
              determinedQueryScopeId = firstSelectedFile.company_id;
              console.log(`[ChatInterface] Query scope overridden to COMPANY (${determinedQueryScopeId}) for shared file.`);
            } else if (firstSelectedFile.team_id && userTeams.some(t => t.id === firstSelectedFile.team_id)) {
              const teamDetails = userTeams.find(t => t.id === firstSelectedFile.team_id);
              // Ensure the team is within the user's primary company context if company context is relevant for teams
              if (teamDetails && userCompany && teamDetails.company_id === userCompany.id) {
                determinedQueryScopeType = 'team';
                determinedQueryScopeId = firstSelectedFile.team_id;
                console.log(`[ChatInterface] Query scope overridden to TEAM (${determinedQueryScopeId}) for shared file.`);
              } else if (teamDetails && !userCompany) { // User has teams but no primary company context, allow team scope
                determinedQueryScopeType = 'team';
                determinedQueryScopeId = firstSelectedFile.team_id;
                console.log(`[ChatInterface] Query scope overridden to TEAM (${determinedQueryScopeId}) for shared file (user has no primary company set).`);
              } else {
                console.warn(`[ChatInterface] Selected shared file, but could not determine a valid company/team query scope consistent with user's affiliations. Falling back to session scope.`);
                // Fallback to session's scope, which might be 'personal' and fail for this shared file.
              }
            } else {
              console.warn(`[ChatInterface] Selected shared file has no company/team ID or doesn't match user's affiliations. Falling back to session scope.`);
            }
          } else {
            console.log("[ChatInterface] All selected files appear personal or first selected is personal. Using session scope or defaulting to personal for query.");
            // If all files are personal, session scope is fine. If session scope is more specific (e.g. team), respect that if file is also in that team.
            // This part can be refined: if session is teamX, and file is personal, should query be personal or teamX for that file?
            // For now, if all seem personal, the original session scope is maintained unless it was invalid.
            if (determinedQueryScopeType !== 'personal' && determinedQueryScopeType !== 'company' && determinedQueryScopeType !== 'team') {
              determinedQueryScopeType = 'personal';
              determinedQueryScopeId = null;
            }
          }
        }
      }
      // If no files are selected for RAG mode, it's an error handled below.

      const active_scope_type = determinedQueryScopeType;
      const active_scope_id = determinedQueryScopeId;

      console.log(`[ChatInterface] handleAskQuestion - FINAL Query Scope: Type='${active_scope_type}', ID='${active_scope_id}'`);

      // MODIFIED: Check based on selected_file_hashes length for RAG mode
      if (!directMode && selected_file_hashes.length === 0) {
        setError(
          <span>
            Please select at least one file from <strong>Knowledge Base</strong> (left navigation).{' '}
            Or, if you haven’t uploaded any files yet,{' '}
            <Button size="small" variant="text" onClick={() => navigate('/upload')} sx={{ textTransform: 'none', ml: 0.5, p: 0 }}>
              upload files
            </Button>{' '}
            first.
          </span>
        );
        return;
      }

      const userMessage = {
        sender: 'user',
        text: currentQuestion,
        mode: directMode ? 'direct' : 'rag'
      };
      setSessions(prev => ({
        ...prev,
        [activeSessionId]: {
          ...prev[activeSessionId],
          conversation: [...(prev[activeSessionId]?.conversation || []), userMessage]
        }
      }));

      // Asynchronously request backend to auto-generate a smart title from the first message
      try {
        const titleToken = token; // reuse
        axios.post(`${BACKEND_URL}/api/sessions/${activeSessionId}/auto-title`, { first_message: currentQuestion }, {
          headers: { Authorization: `Bearer ${titleToken}` }
        }).catch(() => { });
      } catch (_) { }

      if (questionInputRef.current) questionInputRef.current.value = '';
      setQuestion('');
      setLoading(true);
      setIsWebSearching(false);
      setError('');

      // Construct WebSocket URL dynamically based on current location
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = window.location.host;
      const endpoint = directMode
        ? `${wsProtocol}//${wsHost}/ws/direct_question`
        : `${wsProtocol}//${wsHost}/ws/answer_question`;
      // New request, reset stop flag so errors are not suppressed unintentionally
      stopRequestedRef.current = false;
      const ws = new WebSocket(endpoint);
      wsRef.current = ws;

      ws.onopen = () => {
        const messagePayload = directMode ? {
          token: token,
          question: currentQuestion,
          session_id: activeSessionId,
          active_scope_type: active_scope_type,
          active_scope_id: active_scope_id,
          use_web_search: useWebSearch === true
        } : { // RAG mode
          token: token,
          question: currentQuestion,
          selected_file_hashes: selected_file_hashes, // MODIFIED: Send hashes
          session_id: activeSessionId,
          active_scope_type: active_scope_type,
          active_scope_id: active_scope_id,
          rerank_backend: (process.env.REACT_APP_RERANK_BACKEND || 'local'),
          use_web_search: useWebSearch === true
        };

        console.log('[ChatInterface] Sending WebSocket messagePayload:', JSON.stringify(messagePayload));
        ws.send(JSON.stringify(messagePayload));
      };

      ws.onmessage = (event) => {
        console.log("[WebSocket Received RAW]:", event.data);
        try {
          const data = JSON.parse(event.data);
          console.log("[WebSocket Received PARSED]:", data);

          if (data.error) {
            console.error('Error:', data.error);
            setError(data.error);
            ws.close(); // Close connection on error
          } else if (data.token) { // Handle streaming tokens
            // Detect web search sentinel and show UI indicator instead of appending
            if (data.token === '⟪WEB_SEARCH_START⟫') {
              setIsWebSearching(true);
              return; // Do not append to assistant message
            }
            // Any regular token means we're producing output; clear searching flag
            if (isWebSearching) {
              setIsWebSearching(false);
            }
            // First content token → hide the loader
            setLoading(false);
            setSessions(prev => {
              if (!prev[data.session_id]) return prev;

              const conversation = [...(prev[data.session_id]?.conversation || [])];
              let lastMessage = conversation[conversation.length - 1];

              // If the last message is from the user, add a new empty assistant message
              if (!lastMessage || lastMessage.sender !== 'assistant') {
                lastMessage = {
                  sender: 'assistant',
                  text: '',
                  mode: directMode ? 'direct' : 'rag'
                };
                conversation.push(lastMessage);
              }

              // Append the new token
              lastMessage.text += data.token;

              return {
                ...prev,
                [data.session_id]: {
                  ...prev[data.session_id],
                  conversation: conversation
                }
              };
            });
          } else if (data.answer) { // Handle non-streaming full answer for compatibility
            setIsWebSearching(false);
            setLoading(false);
            const assistantMessage = {
              sender: 'assistant',
              text: data.answer,
              mode: directMode ? 'direct' : 'rag'
            };
            setSessions(prev => {
              if (!prev[data.session_id]) return prev;
              return {
                ...prev,
                [data.session_id]: {
                  ...prev[data.session_id],
                  conversation: [...(prev[data.session_id]?.conversation || []), assistantMessage]
                }
              }
            });
            ws.close();
          }
        } catch (parseError) {
          console.error("Failed to parse WebSocket message:", event.data, parseError);
          setError("Received invalid message format from server.");
          ws.close();
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        if (!stopRequestedRef.current) {
          setError('An error occurred with the WebSocket connection.');
        }
        setLoading(false);
        setIsWebSearching(false);
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
          ws.close();
        }
      };

      ws.onclose = (event) => {
        wsRef.current = null;
        console.log('WebSocket connection closed', event);
        setLoading(false);
        setIsWebSearching(false);
        if (!stopRequestedRef.current) {
          // Only show an error for abnormal closures
          if (event && event.code && event.code !== 1000 && event.code !== 1001) {
            setError('Connection closed unexpectedly.');
          }
        }
      };
    } else {
      console.error('User is not authenticated or question is empty.');
      setError('Please log in and enter a question.');
    }
  };

  // --- Function to handle opening file link ---
  const handleOpenFile = async (storagePath, filename) => {
    if (!storagePath) {
      console.error("No storage path available for file:", filename);
      setError(`Could not get storage path for ${filename}.`);
      return;
    }

    setError(''); // Clear previous errors
    setMessage('Generating secure link...'); // Provide feedback
    document.body.style.cursor = "wait";

    try {
      const token = await getAuthToken(); // Use defined token function
      if (!token) {
        throw new Error("Authentication token not available.");
      }

      // Encode the storage path component properly for the URL
      const encodedStoragePath = encodeURIComponent(storagePath);
      const url = `${BACKEND_URL}/get_signed_url/${encodedStoragePath}`; // Use defined BACKEND_URL

      console.log("Requesting signed URL from:", url);

      const response = await axios.get(url, { // Assuming axios is imported
        headers: {
          Authorization: `Bearer ${token}`,
        },
        timeout: 30000 // 30 second timeout
      });

      if (response.data && response.data.signedUrl) {
        console.log("Received signed URL:", response.data.signedUrl);
        window.open(response.data.signedUrl, '_blank', 'noopener,noreferrer');
        setMessage(''); // Clear generating message
      } else {
        throw new Error("Failed to retrieve signed URL from backend.");
      }

    } catch (err) {
      console.error('Error getting signed URL:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Could not open file.';
      setError(`Error opening file ${filename}: ${errorMsg}`);
      setMessage(''); // Clear generating message
    } finally {
      document.body.style.cursor = "default";
    }
  };
  // -----------------------------------------

  // --- NEW: Rename/Delete Handlers --- 
  const handleOpenRenameDialog = useCallback((sessionId) => {
    const currentName = sessions[sessionId]?.name || '';
    setSessionToRename({ id: sessionId, currentName: currentName });
    setNewSessionName(currentName); // Pre-fill with current name
    setRenameError(''); // Clear previous errors
    setIsRenameDialogOpen(true);
  }, [sessions]);

  const handleCloseRenameDialog = () => {
    setIsRenameDialogOpen(false);
    setSessionToRename(null);
    setNewSessionName('');
    setRenameError('');
  };

  const handleRenameSession = async () => {
    if (!sessionToRename || !newSessionName.trim()) {
      setRenameError("New name cannot be empty.");
      return;
    }
    if (newSessionName.trim() === sessionToRename.currentName) {
      handleCloseRenameDialog(); // No change, just close
      return;
    }

    setIsRenaming(true);
    setRenameError('');
    const sessionId = sessionToRename.id;
    const finalNewName = newSessionName.trim();

    try {
      const token = await getAuthToken();
      const response = await axios.put(`${BACKEND_URL}/api/sessions/${sessionId}`,
        { name: finalNewName }, // Send only the name in the update payload
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Update frontend state on success
      setSessions(prev => ({
        ...prev,
        [sessionId]: { ...prev[sessionId], name: finalNewName }
      }));
      handleCloseRenameDialog();

    } catch (error) {
      console.error("Error renaming session:", error);
      const detail = error.response?.data?.detail || "Could not rename session.";
      setRenameError(detail);
      if (error.response?.status === 401 || error.response?.status === 403) {
        if (handleLogout) handleLogout();
      }
    } finally {
      setIsRenaming(false);
    }
  };

  const handleDeleteSession = useCallback(async (sessionId) => {
    // Confirmation dialog
    if (window.confirm(`Are you sure you want to permanently delete this chat session? This action cannot be undone.`)) {
      console.log(`[ChatInterface] Attempting to delete session: ${sessionId}`);
      setError('');
      setMessage('Deleting session...');

      try {
        if (!userCompany || !userCompany.id) {
          throw new Error("Cannot determine company to check deletion permission.");
        }

        // 1. Check for permission from the active company subscription
        const { data: subscription, error: subError } = await supabase
          .from('company_subscriptions')
          .select('allow_manual_session_deletion')
          .eq('company_id', userCompany.id)
          .eq('status', 'active')
          .maybeSingle();

        if (subError) throw new Error(`Failed to verify deletion permission: ${subError.message}`);

        if (!subscription || subscription.allow_manual_session_deletion === false) {
          throw new Error("Your current subscription plan does not allow deleting sessions.");
        }

        // 2. If allowed, call the RPC to delete and decrement quota
        const { error: deleteError } = await supabase
          .rpc('delete_session_and_decrement_quota', {
            p_session_id: sessionId,
            p_company_id: userCompany.id
          });

        if (deleteError) throw new Error(`Failed to delete session: ${deleteError.message}`);

        console.log(`Session ${sessionId} deleted and quota updated. Refreshing session list.`);

        // After successful deletion, refresh the session list.
        // The fetchSessions function will handle activating the most recent session.
        await fetchSessions();

      } catch (error) {
        console.error("[ChatInterface] Error during session deletion:", error);
        setError(error.message || "Could not delete session.");
      } finally {
        setTimeout(() => setMessage(''), 3000);
      }
    }
  }, [sessions, getAuthToken, handleLogout, fetchSessions, userCompany]);
  // -----------------------------------

  const handleNavigateToUpload = () => {
    navigate('/upload');
  };

  // Allow user to stop an in-flight generation/search
  const handleStop = useCallback(() => {
    try {
      stopRequestedRef.current = true;
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        try {
          // Best-effort: inform backend we want to stop (ignored if not implemented)
          wsRef.current.send(JSON.stringify({ type: 'stop', session_id: activeSessionId }));
        } catch (_) { }
      }
      if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
        try { wsRef.current.close(1000, 'User stopped'); } catch (_) { wsRef.current.close(); }
      }
    } catch (e) {
      console.warn('[ChatInterface] Stop pressed but closing WS failed/ignored:', e);
    }
    setLoading(false);
    setIsWebSearching(false);
    setMessage('');
    setError('');
  }, [activeSessionId]);

  // Add a log whenever activeSessionId state changes
  useEffect(() => {
    console.log(`[ChatInterface Effect - activeSessionId Changed] New activeSessionId: ${activeSessionId}`);
  }, [activeSessionId]);

  // --- NEW: Function to handle updating session scope --- 
  const handleSessionScopeUpdate = useCallback(async (sessionId, newScopeType, newScopeId) => {
    if (!sessionId) {
      setError("No session ID provided for scope update.");
      return;
    }
    console.log(`[ChatInterface] handleSessionScopeUpdate called for session: ${sessionId}, newScopeType: ${newScopeType}, newScopeId: ${newScopeId}`);

    // 1. TODO: Implement UI to actually select newScopeType and newScopeId if not directly passed
    //    (e.g., open a dialog pre-filled with current scope, allowing user to change)
    //    For now, this function assumes newScopeType and newScopeId are what we want to set.

    // 2. Call Backend API to update the session scope
    setMessage(`Updating scope for session ${sessionId.substring(0, 4)}...`);
    setError('');
    try {
      const token = await getAuthToken();
      const response = await axios.put(
        `${BACKEND_URL}/api/sessions/${sessionId}`,
        { scope_type: newScopeType, scope_id: newScopeId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data) {
        // 3. Update local session state to reflect the change
        setSessions(prev => ({
          ...prev,
          [sessionId]: {
            ...prev[sessionId],
            scope_type: newScopeType, // Update scope_type in local state
            scope_id: newScopeId,     // Update scope_id in local state
            name: response.data.name, // Also update name if backend returns it, for consistency
            updated_at: response.data.updated_at // And other relevant fields
          }
        }));
        setMessage(`Session ${response.data.name || sessionId.substring(0, 4)} scope updated.`);
        console.log(`[ChatInterface] Successfully updated scope for session ${sessionId} to type: ${newScopeType}, id: ${newScopeId}`);
      } else {
        throw new Error("Invalid response from scope update API");
      }
    } catch (error) {
      console.error("[ChatInterface] Error updating session scope:", error);
      const detail = error.response?.data?.detail || "Could not update session scope.";
      setError(detail);
      if (error.response?.status === 401 || error.response?.status === 403) {
        if (handleLogout) handleLogout();
      }
    } finally {
      setTimeout(() => setMessage(''), 3000);
    }
  }, [getAuthToken, handleLogout, sessions, activeSessionId]); // Dependencies
  // --- END NEW ---

  // --- NEW: Handlers for File Content Dialog ---
  const handleViewFileContent = async (file) => {
    console.log("[handleViewFileContent] Called with file:", file);
    console.log("[handleViewFileContent] File keys:", Object.keys(file));

    // Backend returns 'file_id' not 'id'
    const fileId = file.id || file.file_id;

    if (!file || !fileId) {
      console.error("[handleViewFileContent] Invalid file - no id or file_id. File object:", file);
      setError("Invalid file selected to view content. Missing file ID.");
      return;
    }

    setSelectedFileForContent(file);
    setIsContentDialogOpen(true);
    setLoadingFileContent(true);
    setSelectedFileContent('');

    try {
      console.log("[handleViewFileContent] Fetching content for file id:", fileId);

      // Try to get content from Supabase user_files table
      const { data, error } = await supabase
        .from('user_files')
        .select('extracted_full_text, file_hash, file_name')
        .eq('id', fileId)
        .single();

      console.log("[handleViewFileContent] Supabase query result:", { data, error });

      if (error) {
        console.error("[handleViewFileContent] Supabase error:", error);
        throw error;
      }

      if (!data) {
        throw new Error("No data returned from database");
      }

      // Check if extracted_full_text exists and has content
      if (data.extracted_full_text && data.extracted_full_text.trim()) {
        console.log("[handleViewFileContent] Found extracted_full_text, length:", data.extracted_full_text.length);
        setSelectedFileContent(data.extracted_full_text);
      } else {
        console.warn("[handleViewFileContent] No extracted_full_text found for file:", file.filename || file.file_name);
        const fileName = data.file_name || data.filename || file.filename || file.file_name || 'Unknown';
        setSelectedFileContent(`No extracted text content available for this file.\n\nFile: ${fileName}\nHash: ${data.file_hash}\n\nThe file may not have been processed for text extraction yet, or it may be a binary file without extractable text.`);
      }

    } catch (err) {
      console.error("[handleViewFileContent] Error fetching file content:", err);
      const errorDetails = err.message || err.toString();
      const fileName = file.filename || file.file_name || 'Unknown';
      setSelectedFileContent(`Failed to load file content.\n\nError: ${errorDetails}\n\nFile ID: ${fileId}\nFilename: ${fileName}\n\nPlease check:\n1. The file has been fully processed\n2. The database column 'extracted_full_text' exists\n3. You have permission to access this file`);
    } finally {
      setLoadingFileContent(false);
      console.log("[handleViewFileContent] Completed");
    }
  };

  const handleCloseContentDialog = () => {
    setIsContentDialogOpen(false);
    setSelectedFileForContent(null);
    setSelectedFileContent('');
  };
  // --- END NEW ---

  // Render the component UI
  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <LeftNavDrawer
        open={navSidebarOpen}
        onClose={() => setNavSidebarOpen(false)}
        onCreateSession={async () => { await handleCreateSession(); setNavSidebarOpen(false); }}
        onOpenFiles={() => { setFileListSidebarOpen(true); setNavSidebarOpen(false); }}
        sessions={React.useMemo(() => {
          try {
            const arr = Object.values(sessions || {});
            // sort by updated_at desc if present, else by created_at desc
            arr.sort((a, b) => {
              const au = new Date(a.updated_at || a.created_at || 0).getTime();
              const bu = new Date(b.updated_at || b.created_at || 0).getTime();
              return bu - au;
            });
            return arr.map(s => ({ id: s.id, name: s.name }));
          } catch (_) { return []; }
        }, [sessions])}
        onSelectSession={(id) => { handleSelectSession(id); setNavSidebarOpen(false); }}
        onRenameSession={(id) => { setNavSidebarOpen(false); handleOpenRenameDialog(id); }}
        onDeleteSession={(id) => { setNavSidebarOpen(false); handleDeleteSession(id); }}
        onOpenInvite={() => { setLeftNavInviteOpen(true); }}
        onOpenChatwootSettings={() => { setLeftNavChatwootOpen(true); }}
        onOpenLocalChatwootSettings={() => { setLeftNavLocalChatwootOpen(true); }}
        onOpenUpload={() => { navigate('/upload'); }}
        onOpenUsageBilling={() => { navigate('/billing'); }}
        onOpenCalCom={() => { const bookingUrl = process.env.REACT_APP_BOOKING_URL || (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_BOOKING_URL); if (bookingUrl) window.open(bookingUrl, '_blank', 'noopener,noreferrer'); }}
        onOpenFAQManagement={() => { navigate('/faq'); }}
        onOpenEcommerceIntegrations={() => { navigate('/my-stores'); }}
        onOpenSessionSettings={handleOpenSettingsSidebar}
        onOpenDefaultPrompt={handleOpenDefaultPrompt}
        onLogout={handleLogout}
      />

      {/* Legacy SessionSidebar removed; new LeftNavDrawer is the primary navigation. */}

      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, height: '100vh' }}>
        <AppBar position="static" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, background: '#fff', borderBottom: '1px solid #e0e0e0' }}>
          <Toolbar variant="dense">
            {/* --- Main navigation hamburger SWAPPED to Settings Icon --- */}
            <Tooltip title="Session Settings">
              <IconButton
                sx={{ mr: 1, color: '#000' }}
                aria-label="toggle settings"
                onClick={() => { setSettingsOpen(prev => !prev); }}
                edge="start"
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
            {/* --- END --- */}

            {/* Removed legacy file-list icon; use Knowledge Base in left nav */}

            {/* Session Title */}
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, color: '#000' }}>
              {isLoadingSessions ? "Loading..." : (sessions[activeSessionId]?.name || 'Chat')}
            </Typography>

            {/* Removed legacy New Chat button; use Chat (New) in left nav */}

            {/* User Menu Replaced by Hamburger Menu Icon triggering Right Drawer */}
            <IconButton
              sx={{ ml: 2, color: '#000' }}
              onClick={() => setNavSidebarOpen(prev => !prev)}
            >
              <MenuIcon />
            </IconButton>

            {/* Conditionally render Admin Panel Button */}
            {isAdmin && (
              <Tooltip title="Admin Panel">
                <IconButton
                  sx={{ color: '#000' }}
                  aria-label="go to admin panel"
                  onClick={() => navigate('/admin')}
                >
                  <AdminPanelSettingsIcon />
                </IconButton>
              </Tooltip>
            )}
          </Toolbar>
        </AppBar>

        {console.log(`[ChatInterface RENDER] Passing to UserSettingsSidebar: open=${settingsOpen}, userId=${userId}, activeSessionId=${activeSessionId}`)}
        <UserSettingsSidebar
          open={settingsOpen}
          onClose={handleCloseSettingsSidebar}
          userId={userId}
          activeSessionId={activeSessionId}
          onOpenEcommerceIntegrations={() => { navigate('/my-stores'); }}
          onOpenCalCom={() => { const bookingUrl = process.env.REACT_APP_BOOKING_URL; if (bookingUrl) window.open(bookingUrl, '_blank', 'noopener,noreferrer'); }}
          onOpenLocalChatwootSettings={(integrationId) => {
            console.log("Opening local chatwoot settings for:", integrationId);
            // Ensure function exists in scope or define behavior here
            if (!integrationId) {
              setLeftNavLocalChatwootOpen(true);
            } else {
              // Logic to open settings for specific integration (likely same dialog with prefill)
              // For now, toggle boolean is handled by state
              setLeftNavLocalChatwootOpen(true);
            }
          }}
        />

        <DefaultPromptModal
          open={defaultPromptModalOpen}
          onClose={handleCloseDefaultPrompt}
          userId={userId}
        />

        <Drawer
          variant="temporary"
          open={fileListSidebarOpen}
          onClose={toggleFileListSidebar(false)}
          ModalProps={{
            keepMounted: false,
          }}
        >
          <Box
            sx={{ width: 370, marginTop: '30px' }}
            role="presentation"
            onKeyDown={toggleFileListSidebar(false)}
          >
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Knowledge Base</Typography>

              {/* Select All Checkbox - Above Dropdown */}
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  checked={uploadedFiles.length > 0 && sessions[activeSessionId]?.selectedFiles?.length === uploadedFiles.filter(f => f.processing_status === 'completed').length}
                  indeterminate={sessions[activeSessionId]?.selectedFiles?.length > 0 && sessions[activeSessionId]?.selectedFiles?.length < uploadedFiles.filter(f => f.processing_status === 'completed').length}
                  onChange={(e) => handleSelectAllFiles(e.target.checked)}
                  disabled={uploadedFiles.length === 0}
                />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Select All
                </Typography>
              </Box>

              {/* Team Selection Dropdown */}
              {availableTeams.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="team-select-label">Select by team</InputLabel>
                    <Select
                      labelId="team-select-label"
                      value=""
                      label="Select by team"
                      onChange={(e) => handleSelectFilesByTeam(e.target.value)}
                    >
                      {availableTeams.map((team) => (
                        <MenuItem key={team.id} value={team.id}>
                          {team.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
            </Box>
            <TableContainer component={Paper}>
              <Table aria-label="uploaded files table" size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      {/* Empty header cell for consistency */}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Filename</Typography>
                        <Button size="small" variant="text" onClick={() => { navigate('/files'); }}>
                          All files
                        </Button>
                      </Box>
                    </TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(uploadedFiles) && uploadedFiles.length > 0 ? (
                    uploadedFiles.map((file, index) => (
                      <TableRow
                        key={file.id || index}
                        sx={{
                          backgroundColor: file.processing_status === 'error' ? '#ffebee' :
                            file.processing_status === 'completed' ? '#f1f8e9' :
                              'inherit'
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={sessions[activeSessionId]?.selectedFiles?.includes(file.filename) || false}
                            onChange={(event) => handleCheckboxChange(event, file.filename)}
                            disabled={file.processing_status !== 'completed'}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            noWrap
                            title={file.filename}
                            onClick={() => {
                              if (isHttpUrl(file.source_url)) {
                                window.open(file.source_url, '_blank', 'noopener,noreferrer');
                              } else {
                                handleOpenFile(file.storage_path, file.filename);
                              }
                            }}
                            sx={{
                              cursor: file.storage_path || isHttpUrl(file.source_url) ? 'pointer' : 'default',
                              textDecoration: file.storage_path || isHttpUrl(file.source_url) ? 'underline' : 'none',
                              color: file.storage_path || isHttpUrl(file.source_url) ? 'primary.main' : 'inherit'
                            }}
                          >
                            {file.filename}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" display="block">
                            {new Date(file.created_at).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            color={file.processing_status === 'error' ? 'error' :
                              file.processing_status === 'completed' ? 'success' :
                                'primary'}
                          >
                            {file.processing_status}
                          </Typography>
                          {file.chunk_count > 0 && (
                            <Typography variant="caption" color="textSecondary" display="block">
                              {file.chunk_count} chunks
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton
                              size="small"
                              title={file.error_message || 'View file details'}
                              onClick={() => {
                                if (file.error_message) {
                                  setError(file.error_message);
                                }
                              }}
                            >
                              {file.error_message ?
                                <Typography color="error" variant="caption">!</Typography> :
                                <Typography variant="caption">i</Typography>
                              }
                            </IconButton>

                            <Tooltip title="View Text Content">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewFileContent(file)}
                                  disabled={file.processing_status !== 'completed'}
                                >
                                  <DescriptionIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>

                            {/* This icon logic can remain as a separate visual indicator */}
                            {file.source_url && isHttpUrl(file.source_url) && (
                              <Tooltip title={`Source: ${file.source_url}`}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation(); // Prevent the Typography's onClick from firing
                                    window.open(file.source_url, '_blank', 'noopener,noreferrer');
                                  }}
                                >
                                  <LinkIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" color="textSecondary">
                          No files uploaded yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Drawer>

        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            p: 2,
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: '800px',
              margin: 'auto',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: 'calc(100% - 32px)',
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                flexGrow: 1,
                width: '100%',
                overflowY: 'auto',
                mb: 2,
              }}
            >
              <MessageList conversation={sessions[activeSessionId]?.conversation ?? STABLE_EMPTY_ARRAY} />
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mt: 2,
                minHeight: '40px',
                visibility: loading ? 'visible' : 'hidden'
              }}>
                {loading && (
                  <>
                    <CircularProgress size={24} sx={{ mr: 2 }} />
                    <Typography variant="body1">
                      {isWebSearching ? 'Searching the web...' : 'Thinking for the answer...'}
                    </Typography>
                  </>
                )}
              </Box>
              <div ref={conversationEndRef} />
            </Box>

            {error && (
              <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box
              component="form"
              onSubmit={handleAskQuestion}
              sx={{ mt: 'auto' }}
            >
              <Box sx={{ position: 'relative', flexGrow: 1, width: '100%', mt: 1 }}>

                <TextField
                  label={directMode ? "Your Question" : "Your Question"}
                  placeholder={directMode ? "Ask the LLM anything..." : "Ask a question about your documents..."}
                  multiline
                  minRows={2}
                  maxRows={4}
                  variant="outlined"
                  fullWidth
                  required
                  inputRef={questionInputRef}
                  defaultValue=""
                  inputProps={{ spellCheck: false, autoCorrect: 'off', autoCapitalize: 'none' }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleAskQuestion(e);
                    }
                  }}
                  sx={{
                    '& fieldset': {
                      borderRadius: '16px',
                      borderColor: directMode ? '#1976d2' : 'rgba(0, 0, 0, 0.23)',
                      borderWidth: directMode ? '2px' : '1px'
                    },
                    '& .MuiInputLabel-root': {
                      color: directMode ? '#1976d2' : 'rgba(0, 0, 0, 0.6)'
                    },
                    '& .MuiInputBase-root': {
                      paddingBottom: '36px',
                      paddingRight: '48px'
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box sx={{
                          position: 'absolute',
                          left: 10,
                          bottom: 2,
                          display: 'flex',
                          alignItems: 'center',
                          padding: '2px 5px',
                          borderRadius: '4px',
                          backgroundColor: 'transparent',
                          transition: 'background-color 0.3s ease',
                          zIndex: 10
                        }}>
                          {/* Knowledge Base pill */}
                          <Button
                            size="small"
                            variant={!directMode ? 'contained' : 'outlined'}
                            color="primary"
                            onClick={() => setDirectMode(prev => !prev)}
                            sx={{
                              borderRadius: '9999px',
                              textTransform: 'none',
                              height: 28,
                              minWidth: 0,
                              px: 1.5,
                              mr: 1
                            }}
                          >
                            Knowledge Base
                          </Button>

                          {/* Search pill */}
                          <Button
                            size="small"
                            variant={useWebSearch ? 'contained' : 'outlined'}
                            color="primary"
                            onClick={() => setUseWebSearch(prev => !prev)}
                            sx={{
                              borderRadius: '9999px',
                              textTransform: 'none',
                              height: 28,
                              minWidth: 0,
                              px: 1.5,
                              mr: 1
                            }}
                          >
                            Search
                          </Button>

                          {/* Delete button moved after Search */}
                          <Tooltip title="Delete Current Chat Session" placement="top">
                            <IconButton
                              size="small"
                              onClick={handleDeleteChatHistory}
                              disabled={!activeSessionId}
                              sx={{ ml: 1, color: 'text.secondary' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {/* --- ADD Attachment Button --- */}
                          <Tooltip title="Upload / Manage Files" placement="top">
                            <IconButton
                              size="small"
                              onClick={handleNavigateToUpload}
                              sx={{ ml: 0.5, color: 'text.secondary' }} // Adjusted margin slightly
                            >
                              <AttachFileIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {/* --- END Attachment Button --- */}

                        </Box>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  type={loading ? 'button' : 'submit'}
                  onClick={loading ? handleStop : handleAskQuestion}
                  sx={{
                    position: 'absolute',
                    right: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    borderRadius: '50%',
                    width: '36px',
                    height: '36px',
                    minWidth: '36px',
                    background: 'transparent',
                    border: '1px solid #1976d2',
                    color: loading ? '#d32f2f' : '#1976d2',
                    cursor: 'pointer',
                    padding: 0,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    zIndex: 10,
                    '&:hover': {
                      background: loading ? 'rgba(211, 47, 47, 0.08)' : 'rgba(25, 118, 210, 0.08)',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                    }
                  }}
                >
                  {loading ? <StopIcon fontSize="small" /> : <ArrowUpwardIcon fontSize="small" />}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* --- Add Rename Dialog --- */}
      <Dialog open={isRenameDialogOpen} onClose={handleCloseRenameDialog}>
        <DialogTitle>Rename Chat Session</DialogTitle>
        <DialogContent>
          {renameError && <Alert severity="error" sx={{ mb: 2 }}>{renameError}</Alert>}
          <TextField
            autoFocus
            required
            margin="dense"
            id="session-name"
            label="New Session Name"
            type="text"
            fullWidth
            variant="standard"
            value={newSessionName}
            onChange={(e) => setNewSessionName(e.target.value)}
            disabled={isRenaming}
            onKeyPress={(e) => { if (e.key === 'Enter') { handleRenameSession(); } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRenameDialog} disabled={isRenaming}>Cancel</Button>
          <Button onClick={handleRenameSession} disabled={isRenaming || !newSessionName.trim() || newSessionName.trim() === sessionToRename?.currentName}>
            {isRenaming ? <CircularProgress size={24} /> : "Rename"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* ----------------------- */}

      {/* --- Add Content View Dialog --- */}
      <Dialog open={isContentDialogOpen} onClose={handleCloseContentDialog} fullWidth maxWidth="md">
        <DialogTitle>Content for: {selectedFileForContent?.filename}</DialogTitle>
        <DialogContent>
          {loadingFileContent ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : (
            <TextField
              multiline
              fullWidth
              rows={20}
              value={selectedFileContent}
              InputProps={{ readOnly: true }}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': { height: '100%', alignItems: 'flex-start' },
                '& .MuiOutlinedInput-input': { height: '100% !important', overflow: 'auto !important' }
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseContentDialog}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* --------------------------- */}

      {/* Drawer-triggered dialogs */}
      <InviteMemberDialog
        open={leftNavInviteOpen}
        onClose={() => setLeftNavInviteOpen(false)}
        onSendInvite={handleSendInviteFromLeftNav}
        teams={userTeams || []}
        companyName={userCompany?.name || ''}
        companyId={userCompany?.id || null}
      />
      <ChatwootConfigDialog
        open={leftNavChatwootOpen}
        onClose={() => setLeftNavChatwootOpen(false)}
      />
      <LocalChatwootSetupDialog
        open={leftNavLocalChatwootOpen}
        onClose={() => setLeftNavLocalChatwootOpen(false)}
      />
    </Box>
  );
}

export default ChatInterface;